import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IoCloseOutline } from 'react-icons/io5';
import { CModalFooter } from '@coreui/react';
import {
  AMENITY_FLUSH_MODAL_PROPS,
  StyledModalBodyV2,
  StyledModalCardV2
} from '../../../../../components/modalV2';
import { Palette } from '../../../../../components/utils';
import { WizardFooterButtonPrimary } from '../../../../../components/WizardV2/wizardStylesV2';
import { changeOpenedModal } from '../../../../../redux/actions/headerModal';
import {
  clearAmenity,
  getBuildingAmenitiesV2,
  syncAmenityImagesV2,
  updateAmenityV2,
  uploadAmenityWaiverDoc
} from '../../../../../redux/actions/amenities';
import { setToast } from '../../../../../redux/actions/window';
import { GET_AMENITY_V2 } from '../../../../../redux/constants';
import { requestPending, requestSuccess } from '../../../../../utils/status';
import LoadingAvatar from '../../../../../components/LoadingAvatar';
import { LOADING_BLUE } from '../../../../../utils/colorCode';
import AddAmenityBasicsStepV2 from './AddAmenityBasicsStepV2';
import AddAmenityBlockingStepV2 from './AddAmenityBlockingStepV2';
import AddAmenityImagesStepV2 from './AddAmenityImagesStepV2';
import AddAmenityPaymentStepV2 from './AddAmenityPaymentStepV2';
import AddAmenityRulesStepV2 from './AddAmenityRulesStepV2';
import AddAmenityScheduleStepV2 from './AddAmenityScheduleStepV2';
import AmenityAccordion from './AmenityAccordion';
import { shouldShowPaymentStep } from './addAmenityWizardConfigV2';
import {
  applyWeekSelectionFromChecks,
  WEEK_DAY_CODES
} from './addAmenityScheduleHelpersV2';
import {
  amenityDefaultValues,
  getAmenityValidationSchema
} from './amenityFormSchemaV2';
import {
  AMENITY_PAYMENT_FORM_FIELDS,
  disableRequirePayment
} from './amenityPaymentConfigV2';
import {
  buildAmenityV2CreatePayload,
  buildAmenityWaiverDocFormData
} from './buildAmenityV2Payload';
import { buildAmenityUpdateMediaToast } from './amenityCreateResultDataV2';
import {
  buildImageManifest,
  createNextUploadKey,
  manifestsEqual
} from '../../../../../components/EntityImagesGallery';
import {
  buildImageSyncFormData,
  mapBackendToFormValues,
  resolveAmenityPrimaryBuilding
} from './updateAmenityMapperV2';
import {
  isAmenityCurrentlyOpen,
  resolveViewingBuildingTimezone
} from './amenitiesListDisplayHelpers';
import { AmenityStatusPill } from './AmenitiesListView';
import {
  UpdateAmenityAccordionStack,
  UpdateAmenityCloseButton,
  UpdateAmenityCoverBanner,
  UpdateAmenityFooterValidationHint,
  UpdateAmenityHeaderActions,
  UpdateAmenityHeaderRow,
  UpdateAmenityHeaderTitle,
  UpdateAmenityStickyHeader
} from './updateAmenityV2Styles';

const EMPTY_IMAGE_MANIFEST = { images: [] };

function UpdateAmenityV2() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const days = useMemo(
    () => [
      { value: 'M', label: t('Monday') },
      { value: 'T', label: t('Tuesday') },
      { value: 'W', label: t('Wednesday') },
      { value: 'TH', label: t('Thursday') },
      { value: 'F', label: t('Friday') },
      { value: 'S', label: t('Saturday') },
      { value: 'SU', label: t('Sunday') }
    ],
    [t]
  );

  const amenitiesLabels = useMemo(
    () => ({
      building: t('Building'),
      type: t('Type'),
      name: t('Amenity name'),
      available_days: t('Available days'),
      start_time: t('Available Start Time'),
      end_time: t('Available End Time'),
      max_reserve_time: t('Max reservation time'),
      min_hours: t('Min Hours'),
      max_hours: t('Max Hours'),
      min_days: t('Min Days'),
      max_days: t('Max Days'),
      allow_booking: t('Allow booking'),
      cost_per_hor: t('Cost per hour'),
      description: t('Description'),
      data: t('Data')
    }),
    [t]
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormHydrated, setIsFormHydrated] = useState(false);
  const [amenityImages, setAmenityImages] = useState([]);
  const [existingSchedules, setExistingSchedules] = useState([]);
  const [waiverFile, setWaiverFile] = useState(null);

  const { globalOrgId } = useSelector((state) => state.Organization);
  const { building, allBuildings } = useSelector((state) => state.building);
  const { amenity, amenityTypes, status } = useSelector((state) => state.amenities);
  const { modal, data: modalData } = useSelector((state) => state.headerModal);
  const amenityId = modalData?.amenityId;
  const primaryBuilding = useMemo(
    () => resolveAmenityPrimaryBuilding(amenity, allBuildings),
    [amenity, allBuildings]
  );

  const hydratedAmenityIdRef = useRef(null);
  const initialImageManifestRef = useRef(EMPTY_IMAGE_MANIFEST);
  const nextUploadKeyRef = useRef(1);
  const initialWaiverNameRef = useRef(null);
  const skipWaiverDirtyRef = useRef(false);

  const amenityImagesRef = useRef(amenityImages);
  amenityImagesRef.current = amenityImages;
  useEffect(
    () => () => {
      amenityImagesRef.current.forEach((img) => {
        if (img.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(img.preview);
        }
      });
    },
    []
  );

  const {
    control,
    setValue,
    handleSubmit,
    reset,
    getValues,
    clearErrors,
    trigger,
    formState: { errors, isValid, isDirty }
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(getAmenityValidationSchema(t)),
    defaultValues: amenityDefaultValues
  });

  const amenityCodeWatch = useWatch({ control, name: 'code' });
  const allowBookingWatch = useWatch({ control, name: 'allow_booking' });
  const requiresPaymentWatch = useWatch({ control, name: 'require_payment' });
  const isBlockingAccordionEnabled = !!allowBookingWatch;
  const isPaymentAccordionEnabled = shouldShowPaymentStep({
    allowBooking: allowBookingWatch,
    requiresPayment: requiresPaymentWatch
  });

  const blockingDisabledTooltip = t('update_amenity_blocking_non_bookable_tooltip', {
    defaultValue: 'Blocking settings do not apply to non-bookable amenities.'
  });

  const paymentDisabledTooltip = !allowBookingWatch
    ? t('update_amenity_payment_non_bookable_tooltip', {
        defaultValue: 'Payment settings do not apply to non-bookable amenities.'
      })
    : t('update_amenity_payment_disabled_tooltip', {
        defaultValue:
          'Enable payments in Booking Rules to configure payment settings.'
      });

  const prevAllowBookingRef = useRef(allowBookingWatch);

  const isImagesDirty = useMemo(
    () =>
      !manifestsEqual(
        initialImageManifestRef.current,
        buildImageManifest(amenityImages)
      ),
    [amenityImages]
  );

  const mapNewImage = useCallback((file) => ({
    id: Math.random().toString(),
    uploadKey: createNextUploadKey(nextUploadKeyRef),
    file,
    preview: URL.createObjectURL(file)
  }), []);

  const isWaiverDirty = useMemo(
    () =>
      waiverFile instanceof File ||
      (waiverFile === null && initialWaiverNameRef.current !== null),
    [waiverFile]
  );

  useEffect(() => {
    setValue('waiver_upload', waiverFile, {
      shouldValidate: true,
      shouldDirty: !skipWaiverDirtyRef.current
    });
    skipWaiverDirtyRef.current = false;
  }, [waiverFile, setValue]);

  useEffect(() => {
    const prev = prevAllowBookingRef.current;
    prevAllowBookingRef.current = allowBookingWatch;
    if (prev !== true || allowBookingWatch !== false) return;
    setValue('allow_multi_day', false, { shouldDirty: true, shouldValidate: true });
    setValue('allow_same_day_multiple_bookings', true, {
      shouldDirty: true,
      shouldValidate: true
    });
    setValue('slot_duration_minutes', 30, { shouldDirty: true, shouldValidate: true });
    setValue('min_reservation_hours', 0, { shouldDirty: true, shouldValidate: true });
    setValue('max_reservation_hours', null, { shouldDirty: true, shouldValidate: true });
    setValue('booking_limits_unit', 0, { shouldDirty: true, shouldValidate: true });
    setValue('min_duration_value', null, { shouldDirty: true, shouldValidate: true });
    setValue('max_duration_value', null, { shouldDirty: true, shouldValidate: true });
    setValue('max_advance_booking', 30, { shouldDirty: true, shouldValidate: true });
    setValue('require_reservation_form', false, { shouldDirty: true, shouldValidate: true });
    disableRequirePayment({ setValue, clearErrors }, { shouldValidate: true });
    setValue('require_manager_approval', false, { shouldDirty: true, shouldValidate: true });
    setValue('enable_post_booking_auto_block', false, { shouldDirty: true, shouldValidate: true });
    // Keep block duration on the same slot grid as the reset slot above.
    setValue('post_booking_auto_block_duration', 30, { shouldDirty: true, shouldValidate: true });
    setValue('post_block_description', '', { shouldDirty: true, shouldValidate: true });
    skipWaiverDirtyRef.current = true;
    setWaiverFile(null);
  }, [allowBookingWatch, setValue, clearErrors]);

  useEffect(() => {
    if (modal !== 'updateAmenityV2') return;
    setIsFormHydrated(false);
    hydratedAmenityIdRef.current = null;
  }, [modal, amenityId]);

  useEffect(() => {
    if (modal !== 'updateAmenityV2') {
      hydratedAmenityIdRef.current = null;
      setIsFormHydrated(false);
      return;
    }
    if (!amenity?.id || status !== requestSuccess(GET_AMENITY_V2)) return;
    if (amenityId != null && Number(amenity.id) !== Number(amenityId)) return;
    if (hydratedAmenityIdRef.current === amenity.id) return;

    hydratedAmenityIdRef.current = amenity.id;
    const { formValues, localState } = mapBackendToFormValues(amenity, days, allBuildings);

    reset(formValues, { keepDefaultValues: false, keepDirty: false });
    setAmenityImages(localState.amenityImages);
    nextUploadKeyRef.current = 1;
    initialImageManifestRef.current = buildImageManifest(localState.amenityImages);
    setExistingSchedules(localState.existingSchedules || []);
    skipWaiverDirtyRef.current = true;
    setWaiverFile(localState.waiverFile || null);
    initialWaiverNameRef.current = localState.waiverFile?.name || null;
    clearErrors('waiver_upload');
    setIsFormHydrated(true);
  }, [amenity, amenityId, modal, status, days, allBuildings, reset, clearErrors]);

  const handleApplyWeekChecks = useCallback(
    async (nextChecks) => {
      applyWeekSelectionFromChecks(nextChecks, {
        setValue,
        dayOptions: days,
        t,
        shouldDirty: true
      });

      WEEK_DAY_CODES.forEach((code) => {
        if (!nextChecks[code]) {
          setValue(`daily_schedule.${code}.start`, null, {
            shouldDirty: true,
            shouldValidate: false
          });
          setValue(`daily_schedule.${code}.end`, null, {
            shouldDirty: true,
            shouldValidate: false
          });
        }
      });

      await trigger([
        'selectedDays',
        'daily_schedule',
        'min_reservation_hours',
        'max_reservation_hours',
        'min_duration_value',
        'max_duration_value'
      ]);
    },
    [setValue, days, t, trigger]
  );

  const resetLocalState = useCallback(() => {
    setAmenityImages((prev) => {
      prev.forEach((img) => {
        if (img.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(img.preview);
        }
      });
      return [];
    });
    initialImageManifestRef.current = EMPTY_IMAGE_MANIFEST;
    nextUploadKeyRef.current = 1;
    initialWaiverNameRef.current = null;
    skipWaiverDirtyRef.current = false;
    setExistingSchedules([]);
    setWaiverFile(null);
    clearErrors('waiver_upload');
  }, [clearErrors]);

  const onClose = useCallback(() => {
    dispatch(changeOpenedModal({ modal: '' }));
    dispatch(clearAmenity());
    hydratedAmenityIdRef.current = null;
    setIsFormHydrated(false);
    reset(amenityDefaultValues);
    resetLocalState();
  }, [dispatch, reset, resetLocalState]);

  const headerTitle = useMemo(() => {
    const name = (amenityCodeWatch || '').trim();
    if (name) {
      return t('update_amenity_header_title', {
        defaultValue: '{{name}} Settings',
        name
      });
    }
    return t('Amenity Settings', { defaultValue: 'Amenity Settings' });
  }, [amenityCodeWatch, t]);

  const coverImageSrc = amenityImages[0]?.preview ?? null;

  const amenityIsOpen = useMemo(() => {
    const statusTimezone = resolveViewingBuildingTimezone(building);
    return isAmenityCurrentlyOpen(amenity?.amenities_schedules, statusTimezone);
  }, [amenity?.amenities_schedules, building]);

  const onSubmit = (values) => {
    if (!amenity?.id || !primaryBuilding?.id) return;
    setIsSubmitting(true);

    const body = buildAmenityV2CreatePayload(values, primaryBuilding, {
      existingSchedules
    });

    dispatch(
      updateAmenityV2({
        id: amenity.id,
        body,
        success: async () => {
          try {
            const needsDocUpload = Boolean(
              values.require_reservation_form && waiverFile instanceof File
            );
            const needsImageSync = !manifestsEqual(
              initialImageManifestRef.current,
              buildImageManifest(amenityImages)
            );

            const runDocUpload = () => {
              if (!needsDocUpload) {
                return Promise.resolve({ role: 'doc', status: 'skipped' });
              }
              return new Promise((resolve) => {
                dispatch(
                  uploadAmenityWaiverDoc({
                    body: buildAmenityWaiverDocFormData({
                      waiverFile,
                      orgId: globalOrgId,
                      buildingId: primaryBuilding.id,
                      entityId: amenity.id
                    }),
                    success: () => resolve({ role: 'doc', status: 'ok' }),
                    fail: () => resolve({ role: 'doc', status: 'failed' })
                  })
                );
              });
            };

            const runImageSync = () => {
              if (!needsImageSync) {
                return Promise.resolve({ role: 'img', status: 'skipped' });
              }
              return new Promise((resolve) => {
                dispatch(
                  syncAmenityImagesV2({
                    body: buildImageSyncFormData({
                      amenityImages,
                      amenityId: amenity.id,
                      orgId: globalOrgId,
                      buildingId: primaryBuilding.id
                    }),
                    success: (res) => {
                      if (res?.data?.failed?.length > 0) {
                        resolve({ role: 'img', status: 'partial' });
                        return;
                      }
                      resolve({ role: 'img', status: 'ok' });
                    },
                    fail: () => resolve({ role: 'img', status: 'failed' })
                  })
                );
              });
            };

            let docOutcome = { status: 'skipped' };
            let imgOutcome = { status: 'skipped' };
            if (needsDocUpload || needsImageSync) {
              const outcomes = await Promise.all([runDocUpload(), runImageSync()]);
              docOutcome = outcomes[0];
              imgOutcome = outcomes[1];
            }

            const { variant, message } = buildAmenityUpdateMediaToast(docOutcome, imgOutcome, t);

            dispatch(
              setToast({
                toastShow: true,
                toastMessage: message,
                ...(variant === 'warning' ? { modal: 'errorToast' } : {})
              })
            );

            if (building?.id) {
              dispatch(getBuildingAmenitiesV2({ id: building.id }));
            }

            onClose();
          } finally {
            setIsSubmitting(false);
          }
        },
        fail: (err) => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: err?.data?.message || t('Failed to update amenity.'),
              modal: 'errorToast'
            })
          );
          setIsSubmitting(false);
        }
      })
    );
  };

  const handleUpdateClick = async () => {
    if (requiresPaymentWatch) {
      const ok = await trigger(AMENITY_PAYMENT_FORM_FIELDS, { shouldFocus: true });
      if (!ok) return;
    }
    void handleSubmit(onSubmit)();
  };

  const hasChanges = isDirty || isImagesDirty || isWaiverDirty;

  const canSubmit = hasChanges && isValid && !isSubmitting;

  const isLoadingAmenity =
    modal === 'updateAmenityV2' &&
    (status === requestPending(GET_AMENITY_V2) ||
      !amenity?.id ||
      (amenityId != null && Number(amenity.id) !== Number(amenityId)));

  const showFormContent =
    !isLoadingAmenity &&
    isFormHydrated &&
    amenityId != null &&
    Number(amenity?.id) === Number(amenityId);

  const showFooterValidationHint =
    hasChanges && !isValid && !isSubmitting && showFormContent;

  if (modal !== 'updateAmenityV2') {
    return null;
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
      }}>
      <StyledModalCardV2
        {...AMENITY_FLUSH_MODAL_PROPS}
        show={modal === 'updateAmenityV2'}
        closeOnBackdrop={false}
        onClose={onClose}
        width="555px">
        {showFormContent ? (
          <>
            <UpdateAmenityStickyHeader>
              <UpdateAmenityHeaderRow>
                <UpdateAmenityHeaderTitle>{headerTitle}</UpdateAmenityHeaderTitle>
                <UpdateAmenityHeaderActions>
                  <AmenityStatusPill isOpen={amenityIsOpen} />
                  <UpdateAmenityCloseButton type="button" onClick={onClose} aria-label={t('Close')}>
                    <IoCloseOutline size={16} color={Palette.CONTENT_TERTIARY} />
                  </UpdateAmenityCloseButton>
                </UpdateAmenityHeaderActions>
              </UpdateAmenityHeaderRow>
            </UpdateAmenityStickyHeader>

            <StyledModalBodyV2 gap="0">
              {coverImageSrc ? (
                <UpdateAmenityCoverBanner src={coverImageSrc} alt="" />
              ) : null}
              <UpdateAmenityAccordionStack>
                <AmenityAccordion title={t('Amenity Details')} defaultOpen>
                  <AddAmenityBasicsStepV2
                    t={t}
                    building={primaryBuilding}
                    control={control}
                    errors={errors}
                    amenityTypes={amenityTypes}
                    setValue={setValue}
                  />
                </AmenityAccordion>

                <AmenityAccordion title={t('Schedule')}>
                  <AddAmenityScheduleStepV2
                    t={t}
                    control={control}
                    errors={errors}
                    amenitiesLabels={amenitiesLabels}
                    getValues={getValues}
                    setValue={setValue}
                    onApplyWeekChecks={handleApplyWeekChecks}
                    dayOptions={days}
                    scheduleMode="singleSlot"
                  />
                </AmenityAccordion>

                <AmenityAccordion title={t('Booking Rules')}>
                  <AddAmenityRulesStepV2
                    t={t}
                    control={control}
                    errors={errors}
                    amenitiesLabels={amenitiesLabels}
                    building={primaryBuilding}
                    allBuildings={allBuildings}
                    waiverFile={waiverFile}
                    setWaiverFile={setWaiverFile}
                    clearErrors={clearErrors}
                    setValue={setValue}
                    trigger={trigger}
                    initialAllowBooking={!!amenity?.allow_booking}
                  />
                </AmenityAccordion>

                <AmenityAccordion
                  title={t('Blocking')}
                  disabled={!isBlockingAccordionEnabled}
                  disabledTooltip={blockingDisabledTooltip}>
                  <AddAmenityBlockingStepV2
                    t={t}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    trigger={trigger}
                    clearErrors={clearErrors}
                  />
                </AmenityAccordion>

                <AmenityAccordion
                  title={t('Payment')}
                  disabled={!isPaymentAccordionEnabled}
                  disabledTooltip={paymentDisabledTooltip}>
                  <AddAmenityPaymentStepV2
                    t={t}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                  />
                </AmenityAccordion>

                <AmenityAccordion title={t('Images')}>
                  <AddAmenityImagesStepV2
                    t={t}
                    amenityImages={amenityImages}
                    setAmenityImages={setAmenityImages}
                    building={primaryBuilding}
                    mapNewImage={mapNewImage}
                  />
                </AmenityAccordion>
              </UpdateAmenityAccordionStack>
            </StyledModalBodyV2>

            <CModalFooter>
              <WizardFooterButtonPrimary
                type="button"
                disabled={!canSubmit}
                onClick={handleUpdateClick}>
                {isSubmitting ? t('Updating...') : t('Update Amenity', { defaultValue: 'Update Amenity' })}
              </WizardFooterButtonPrimary>
              {showFooterValidationHint ? (
                <UpdateAmenityFooterValidationHint>
                  {t('update_amenity_footer_validation_hint', {
                    defaultValue:
                      'Some fields are missing input in the settings above. Please complete them to update.'
                  })}
                </UpdateAmenityFooterValidationHint>
              ) : null}
            </CModalFooter>
          </>
        ) : (
          <LoadingAvatar color={LOADING_BLUE} />
        )}
      </StyledModalCardV2>
    </form>
  );
}

export default UpdateAmenityV2;
